import createCache, { type EmotionCache } from '@emotion/cache';
import DOMPurify from 'dompurify';

/**
 * Helpers that let the site run under a strict Content-Security-Policy
 * (`style-src 'self'`, no 'unsafe-inline'). CSP blocks inline <style> tags and
 * style="" attributes, but not styles applied through the CSSOM, so both
 * paths below go through the CSSOM instead.
 */

const INLINE_STYLE_ATTR = 'data-csp-style';

/**
 * CMS content (CKEditor HTML) carries style="" attributes. DOMPurify moves
 * them to a data attribute and an observer re-applies them via el.style.
 */
export function installCspSafeInlineStyles() {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof Element && node.hasAttribute('style')) {
      node.setAttribute(INLINE_STYLE_ATTR, node.getAttribute('style') || '');
      node.removeAttribute('style');
    }
  });

  const applyTo = (element: Element) => {
    const css = element.getAttribute(INLINE_STYLE_ATTR);
    if (css !== null && 'style' in element) {
      (element as HTMLElement).style.cssText = css;
      element.removeAttribute(INLINE_STYLE_ATTR);
    }
  };

  const applyWithin = (root: Element) => {
    applyTo(root);
    root.querySelectorAll(`[${INLINE_STYLE_ATTR}]`).forEach(applyTo);
  };

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) applyWithin(node);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  applyWithin(document.documentElement);
}

/**
 * Emotion (used by react-select) injects <style> tags at runtime. This cache
 * writes its rules into a constructable stylesheet instead.
 */
export function createCspSafeEmotionCache(): EmotionCache {
  const cache = createCache({ key: 'css' });

  if (typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in Document.prototype)) {
    return cache;
  }

  const sheet = new CSSStyleSheet();
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];

  cache.sheet.insert = (rule: string) => {
    try {
      sheet.insertRule(rule, sheet.cssRules.length);
    } catch {
      // Vendor-prefixed selectors the browser doesn't know are rejected; emotion ignores these too.
    }
  };
  cache.sheet.flush = () => {
    sheet.replaceSync('');
  };
  cache.sheet.hydrate = () => {};

  return cache;
}
